// Purpose: coach (stepper) to distribute a final bill amount over the tenants
// State:
//
//   ** fields for landlord charge to be created
//   amount: copy of totalAmount
//   companyId: copy of company, // counterparty
//   dateOfPeriod: copy of periodFirst
//   description: this.trans("coach/new-bill-settlement/steps/bill/section-one/description/default"),
//   lastDateOfPeriod: copy of periodLast
//   period: PeriodContainer.pickIdFromLabel("once")
//   propertyId: address.data.id
//   type: copy of chargeTypeForLandlord?.value
//
//   ** fields for the registration of bill details
//   address:<Property>
//   chargeTypeForLandlord:{ // the selected chargeType for the bill
//     label:string
//     value:number - id of the chargeType 
//     utilityBill:ChargeType - selected object for the db entity chargeType
//   chargeTypeForTenant:{ // the selected chargeType for the charges to be created for tenants
//     label:string
//     value:number - id of the chargeType 
//     utilityBill:ChargeType - selected object for the db entity chargeType
//   company:number - id of the company that submitted the bill
//   files:[]
//   paidAmount:number - the amount deposited (already paid) by the landlord
//   periodFirst:string - first day of the bill period
//   periodLast:string - last day of the bill period, example: "2023-12-31T23:00:00.000Z"
//   totalAmount:float - bill amount
//
//   ** unit related fields
//   distributionKeys:{<unitId>:number}
//     the numbers (provided by the user) based upon which the bill total is to be distributed

//   ** fields for coach operations
//   activeStep:number
//   administrationPercentageFee:number
//   errors:{<errorKey>:string} - error key is a fieldname or other label (ex. 'period') to identify an error
//   isNextDisabled:boolean - seems to be added to the coach state during one of the steps
//   loading:false - seems to be added to the coach state during one of the steps
//   steps:{0:{}, 1:{}, 2:{}...}
//
//   ** fields for bill related calculations
//   bill_calculations:{
//     administration_percentage_fee:number - the percentage of the bill amount that is the administration fee (which fee should be included in the amount to settle)
//     monthly_payment:float - total amount to be settled distributed over the months of the bill period
//     number_of_billed_months:float - the bill period expressed in calendar months 
//     total_amount:float - bill amount
//     total_settled_amount:float - amount to settle (bill amount plus administration fee)
//   }
//
//   ** fields for unit related calculations
//   unit_calculations:
//     billed_months_by_contract:{<contractId>:float - for each contract the fraction (in months) of the bill period; example: 9.36 months
//     billed_months_charges_by_contract: {<contractId>:{<chargeTemplateId>:float}} - for each contract and each charge template the fraction (in months) of the bill period; example: 4.36 months
//     charges_by_contract: {<contractId>:array[<chargeTemplateId>]} - for each contract the list of charge templates that intersect with the bill period
//     contracts_by_tenant: {<tenantId>:array[index].<Contract> - for each tenant the list of contracts that intersect with the bill period
//     deposited_by_units: {<unitId>:float} - for each unit the total amount deposited for the combined intersecting periods of the contract, charge templates and bill period; example: 412.00 EUR
//     deposited_by_units_by_tenants: {<unitId>:{<tenantId>:float}} - for each unit, for each tenant, the amount deposited for the combined intersecting periods of the contract, charge templates and bill period
//     intersecting_contracts: array[index].<Contract> - all contracts found in intersecting_contracts_by_unit (e.g. all contract to be considered for the calculations)
//     intersecting_contracts_by_unit: {<unitId>:array[index].<Contract>} - for each unit the contracts for which the period intersect with the bill period
//     periods_by_contracts: {<contractId>:array[index].PeriodDetails { - for each contract the effective period (also in months), the total amount to pay for that period and the total amount deposited for that period
//       period: Period{
//         start: Date
//         end: Date
//       billed-months:float - number of months in the overlap of contract and bill
//       deposited:float - amount deposited (already paid) for the contract
//       to_pay:float - amount to pay for the contract
//     }
//     tenants: array[index].<Person> - the details of all tenants (unique) present in intersecting_contracts
//     tenants_by_unit: {<unitId>:array[index].<Person>} - for each unit all tenants found in the contracts with a period that interect with the bill period
//     to_pay_by_units: {<unitId>:float} - for each unit the total amount to pay
//     to_pay_by_units_by_tenants: {<unitId>:{<tenantId>.float}} - for each unit, for each tenant, the total amount to pay
//     unit_percentages: {<unitId>:float} - for each unit the percentage of the total bill amount (including fee) to be paid
//     units:array - all units involved
//       
import * as React from "react";
import { browserHistory } from "react-router";
import { connect } from "react-redux";

import Component from "../../../AbstractComponent";

import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";

import Address from "./steps/Address";
import Bill from "./steps/Bill";
import Unit from "./steps/Unit";
import Tenant from "./steps/Tenant";
import Finish from "./steps/Finish";

import PeriodContainer from "../../../../Entity/Entities/Charge/Period/PeriodContainer";
import Time from "../../../../Time/Time";
import "../../../../../../styles/Components/Coach/NewBillSettlement.scss";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";

const initial_steps = {
  0: {
    name: "address",
    trans_key: "coach/new-bill-settlement/steps/address/title",
    state: {},
    props: {
      completed: false,
    },
    label_props: {},
  },
  1: {
    name: "bill",
    trans_key: "coach/new-bill-settlement/steps/bill/title",
    state: {},
    props: {
      completed: false,
    },
    label_props: {},
  },
  2: {
    name: "unit",
    trans_key: "coach/new-bill-settlement/steps/unit/title",
    state: {},
    props: {
      completed: false,
    },
    label_props: {},
  },
  3: {
    name: "tenant",
    trans_key: "coach/new-bill-settlement/steps/tenant/title",
    state: {},
    props: {
      completed: false,
    },
    label_props: {},
  },
  4: {
    name: "finish",
    trans_key: "coach/new-bill-settlement/steps/finish/title",
    state: {},
    props: {
      completed: false,
    },
    label_props: {},
  },
};

class CoachNewBillSettlement extends Component {
  public state = {
    activeStep: 0,
    steps: initial_steps,

    // Defaults for testing purposes (uncommment when necessary)
    // totalAmount: 50000,
    // paidAmount: 1000,
    // periodFirst: new Date("2019-02-01"),
    // periodLast: new Date(),
    // administrationPercentageFee: 10,
  };

  constructor(public props) {
    super(props);

    this.updateState = this.updateState.bind(this);
    this.onCancel = this.onCancel.bind(this);
  }

  componentDidUpdate(_: Readonly<any>, prevState: Readonly<any>): void {
    console.log("<CoachNewBillSettlement ");
    console.log(" props=", this.props);
    console.log(" state=", this.state);
    console.log("/>");

    // scroll to top smoothly when changing step
    if (prevState.activeStep !== this.state.activeStep) {
      document.getElementById("coach_newBillSettlement_wrapper")?.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }

  public onCancel() {
    // go back to the previous page
    // if (window.history && window.history.back)
    //   window.history.back();
    // else
    //   window.location.href = "/";
    browserHistory.goBack();
  }

  public updateState(state: any) {
    this.setState({
      ...this.state,
      ...state,
    });
    this.setState({ // set the fields for the creation of the bill charge
      amount: state.totalAmount,
      dateOfPeriod: Time.formatOrNull(state.periodFirst),
      lastDateOfPeriod: Time.formatOrNull(state.periodLast),
      type: state.chargeTypeForLandlord?.value,
      companyId: state.company.id, // counterparty
      // ownerId: state.address.data.companyId,
      propertyId: state.address.data.id,
      period: PeriodContainer.pickIdFromLabel("once"),
      description: this.trans("coach/new-bill-settlement/steps/bill/section-one/description/default"),
    })
  }

  render() {
    return (
      <div
        className="coach_newBillSettlement_wrapper"
        id="coach_newBillSettlement_wrapper"
      >
        <div className="coach_newBillSettlement_stepperWrapper">
          <div className="coach_newBillSettlement_stepper">
            <Stepper activeStep={this.state.activeStep || 0}>
              {Object.entries(this.state.steps).map(([key, value]) => {
                return (
                  <Step
                    key={key}
                    {...value.props}
                    completed={this.state.activeStep > parseInt(key)}
                  >
                    <StepLabel {...value.label_props}>
                      {this.trans(value.trans_key)}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </div>

          <div className="coach_newBillSettlement_stepper_cancelButton">
            <IconButton
              aria-label="cancel"
              onClick={this.onCancel}
              className="coach_newBillSettlement_stepper_cancelButton_icon"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className="coach_newBillSettlement_stepWrapper">
          {!this.state.activeStep ? (
            <Address
              {...this.props}
              state={this.state}
              setState={this.updateState}
              onCancel={this.onCancel}
            />
          ) : null}
          {this.state.activeStep == 1 ? (
            <Bill
              {...this.props}
              state={this.state}
              setState={this.updateState}
              onAttachmentsUpdated={attachments => {this.setState({attachments: attachments})}}
            />
          ) : null}
          {this.state.activeStep == 2 ? (
            <Unit
              {...this.props}
              state={this.state}
              setState={this.updateState}
            />
          ) : null}
          {this.state.activeStep == 3 ? (
            <Tenant
              {...this.props}
              state={this.state}
              setState={this.updateState}
            />
          ) : null}
          {this.state.activeStep == 4 ? (
            <Finish
              {...this.props}
              state={this.state}
              setState={this.updateState}
            />
          ) : null}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    app: state.app,
    coach: state.coach,
    globalTypes: state.globalTypes,
    charges: state.charges,
    units: state.units,
    properties: state.properties,
    component: state.unitUpdate,
    chargeTemplateCreate: state.chargeTemplateCreate,
    chargeTemplateUpdate: state.chargeTemplateUpdate,
    agencyFeeCreate: state.agencyFeeCreate,
    agencyFeeUpdate: state.agencyFeeUpdate,
  };
};

export default connect(mapStateToProps)(CoachNewBillSettlement);